import React, { useEffect, useState, useRef } from "react";
import { MapContainer, GeoJSON, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { getGeoRegionalState, getGeoWoreda } from "../../services/apiServices.js";

// Water body distance color categories
const waterColors = {
    "<0.5 km": "#003366",
    "0.5-1 km": "#0073e6",
    "1-2 km": "#3399ff",
    "2-5 km": "#66b2ff",
    ">5 km": "#99ccff",
    "No data": "#cccccc",
};

// Map distance to category
const getWaterCategory = (distance) => {
    if (distance == null || isNaN(distance)) return "No data";
    if (distance < 500) return "<0.5 km";
    if (distance < 1000) return "0.5-1 km";
    if (distance < 2000) return "1-2 km";
    if (distance < 5000) return "2-5 km";
    return ">5 km";
};

// Styles
const woredaStyle = { color: "#009933", weight: 1, fillOpacity: 0.6 };
const regionStyle = { color: "#003366", weight: 1.5, fillOpacity: 0 };
const highlightStyle = { color: "#ff6600", weight: 2, fillOpacity: 0.7 };

// Woreda layer for water bodies
const WaterBodyLayer = ({ data, selectedCategories }) => {
    const map = useMap();
    const geoJsonRef = useRef();

    // Function to compute style
    const getFillStyle = (feature) => {
        const waterBodies = feature.properties?.factors?.water_bodies ?? [];
        const nearestDistance = waterBodies.length
            ? Math.min(...waterBodies.map((w) => w.distance_m))
            : null;
        const category = getWaterCategory(nearestDistance);

        // Check if the category is in the selected categories array
        if (selectedCategories.length && !selectedCategories.includes(category)) {
            return { ...woredaStyle, fillOpacity: 0, opacity: 0 }; // Hide layer if category is not selected
        }

        return {
            ...woredaStyle,
            fillColor: waterColors[category] || "#cccccc",
            fillOpacity: 0.6,
            opacity: 1,
        };
    };

    // Update style when selectedCategories changes
    useEffect(() => {
        if (geoJsonRef.current) {
            geoJsonRef.current.eachLayer((layer) => {
                const style = getFillStyle(layer.feature);
                layer.setStyle(style);
            });
        }
    }, [selectedCategories, data]);

    const onEachFeature = (feature, layer) => {
        const style = getFillStyle(feature);
        layer.setStyle(style);

        layer.on({
            mouseover: () => layer.setStyle({ ...highlightStyle, fillOpacity: 0.7 }),
            mouseout: () => layer.setStyle(getFillStyle(feature)),
        });

        // Popup info
        const waterBodies = feature.properties?.factors?.water_bodies ?? [];
        const nearestDistance = waterBodies.length
            ? Math.min(...waterBodies.map((w) => w.distance_m))
            : null;
        const waterList = waterBodies
            .map(
                (w) =>
                    `• ${w.name || "unknown"} (${w.water_type || "unknown"}, ${Math.round(
                        w.distance_m
                    )} m)`
            )
            .join("<br/>");

        layer.bindPopup(
            `<b>${feature.properties.WOREDANAME}</b><br/>
       Nearest water body: ${nearestDistance ? Math.round(nearestDistance) + " m" : "No data"}<br/>
       Water bodies:<br/>${waterList}`
        );
    };

    return <GeoJSON data={data} onEachFeature={onEachFeature} ref={geoJsonRef} />;
};

// Region layer
const RegionLayer = ({ data }) => {
    const map = useMap();

    const onEachFeature = (feature, layer) => {
        layer.setStyle(regionStyle);
        layer.bindTooltip(feature.properties.REGIONNAME, {
            permanent: true,
            direction: "center",
            className: "region-tooltip",
        });
    };

    return <GeoJSON data={data} onEachFeature={onEachFeature} />;
};

// Legend for water bodies
const Legend = ({ selectedCategories, setSelectedCategories }) => {
    const map = useMap();

    useEffect(() => {
        const legend = L.control({ position: "bottomright" });

        legend.onAdd = () => {
            const div = L.DomUtil.create("div", "info legend");
            div.style.background = "#fff";
            div.style.padding = "16px";
            div.style.borderRadius = "5px";
            div.style.fontFamily = "Poppins, sans-serif";
            div.style.fontSize = "14px";
            div.innerHTML = `<p style="font-weight:700; margin-top:0;">Nearest Water Body</p>`;

            // Clear previous click handlers if any
            div.onclick = (e) => e.stopPropagation(); // prevent map click

            // Add category items
            for (const [category, color] of Object.entries(waterColors)) {
                if (category === "No data") continue;

                const item = document.createElement("div");
                item.style.marginBottom = "4px";
                item.style.cursor = "pointer";
                item.style.textDecoration =
                    selectedCategories.includes(category) ? "underline" : "none"; // Show underline if selected

                item.innerHTML =
                    `<i style="background:${color}; width:18px; height:18px; float:left; margin-right:8px; opacity:0.7; border-radius:3px;"></i>` +
                    category;

                // Bind click using a closure to handle multiple categories
                item.addEventListener("click", () => {
                    setSelectedCategories((prev) => {
                        if (prev.includes(category)) {
                            return prev.filter((cat) => cat !== category); // Deselect
                        } else {
                            return [...prev, category]; // Select
                        }
                    });
                });

                div.appendChild(item);
            }

            // Clear filter button
            if (selectedCategories.length) {
                const clear = document.createElement("div");
                clear.style.cursor = "pointer";
                clear.style.marginBottom = "8px";
                clear.style.color = "#024795";
                clear.style.fontWeight = "600";
                clear.innerText = "Clear Filters";
                clear.addEventListener("click", () => setSelectedCategories([])); // Clear all selections
                div.insertBefore(clear, div.firstChild);
            }

            // Add No data
            const noData = document.createElement("div");
            noData.style.marginTop = "8px";
            noData.innerHTML =
                `<i style="background:${waterColors["No data"]}; width:18px; height:18px; float:left; margin-right:8px; opacity:0.7; border-radius:3px;"></i>` +
                "No data";
            div.appendChild(noData);

            return div;
        };

        legend.addTo(map);
        return () => legend.remove();
    }, [map, selectedCategories, setSelectedCategories]);

    return null;
};


// Main Water Bodies Map
export default function WaterBodiesMap() {
    const [geojsonData, setGeojsonData] = useState(null);
    const [regions, setRegions] = useState(null);
    const [selectedCategories, setSelectedCategories] = useState([]);
    const mapRef = useRef();

    useEffect(() => {
        async function fetchGeoData() {
            try {
                const regionalData = await getGeoRegionalState();
                setRegions({
                    type: "FeatureCollection",
                    features: regionalData.map((r) => ({
                        type: "Feature",
                        geometry: r.geometry,
                        properties: { REGIONNAME: r.regionalState },
                    })),
                });

                getGeoWoreda(100, (batchData) => {
                    setGeojsonData((prevData) => {
                        if (prevData) {
                            return {
                                type: "FeatureCollection",
                                features: [...prevData.features, ...batchData],
                            };
                        } else {
                            return { type: "FeatureCollection", features: batchData };
                        }
                    });
                });
            } catch (err) {
                console.error("Failed to load geo data:", err);
            }
        }
        fetchGeoData();
    }, []);

    return (
        <div style={{ height: "100vh", width: "100%" }}>
            <MapContainer
                center={[9.145, 40.4897]}
                zoom={6}
                scrollWheelZoom
                style={{ height: "100%", width: "100%" }}
                ref={mapRef}
            >
                {regions && <RegionLayer data={regions} />}
                {geojsonData && (
                    <WaterBodyLayer data={geojsonData} selectedCategories={selectedCategories} />
                )}
                <Legend selectedCategories={selectedCategories} setSelectedCategories={setSelectedCategories} />
            </MapContainer>
        </div>
    );
}
